import {
  CircularProgress,
  Skeleton,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { Styles, mergeSx } from '@t2c/shared-fe';
import CustomModal from '../CustomModal';
import { CustomDialogTitle } from './CustomDialogTitle';
import { CustomDialogContainer } from './CustomDialogContainer';

interface Props {
  shouldOpenDialog: boolean;
  title?: string;
  text: string;
  element?: JSX.Element;
  onCancel: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  confirmBtnText?: string;
  cancelBtnText?: string;
  showSkeletonLoader?: boolean;
}

export const ConfirmDialog = ({
  shouldOpenDialog,
  title,
  text,
  onCancel,
  onConfirm,
  isLoading,
  element,
  confirmBtnText,
  cancelBtnText,
  showSkeletonLoader,
}: Props) => {
  return (
    <CustomDialogContainer open={shouldOpenDialog}>
      <CustomDialogTitle
        sx={styles.title}
        title={title || 'Confirm'}
        onClose={onCancel}
        showSkeletonLoader={showSkeletonLoader}
      />
      <DialogContent dividers sx={mergeSx(styles.dialogContent, { py: 5 })}>
        {showSkeletonLoader ? (
          <>
            <Skeleton />
            <Skeleton width="40%" />
          </>
        ) : (
          <>
            <Typography sx={styles.text}>{text}</Typography>
            {element}
          </>
        )}
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        {showSkeletonLoader ? (
          <Stack direction="row" columnGap={1} sx={styles.buttonStack}>
            <Skeleton variant="rectangular" width="30%" height={36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width="30%" height={36} sx={{ borderRadius: 1 }} />
          </Stack>
        ) : (
          <Stack direction="row" columnGap={1} sx={styles.buttonStack}>
            <Button
              variant="outlined"
              size="medium"
              onClick={onCancel}
              sx={styles.button}
              disabled={isLoading}
            >
              {cancelBtnText || 'Cancel'}
            </Button>
            <Button
              variant="contained"
              size="medium"
              onClick={onConfirm}
              sx={styles.confirmButton}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={18} /> : confirmBtnText || 'Yes'}
            </Button>
          </Stack>
        )}
      </DialogActions>
    </CustomDialogContainer>
  );
};

const styles: Styles = {
  title: {
    color: (theme) => theme.palette.text.primary,
    paddingBottom: '10px',
    paddingTop: '10px',
  },
  dialogContent: {
    py: 1,
    marginLeft: '10%',
    marginRight: '10%',
    backgroundColor: (theme) => theme.palette.background.secondary,
  },
  text: {
    fontSize: 16,
    color: (theme) => theme.palette.text.primary,
    textAlign: 'center',
    fontWeight: 400,
    lineHeight: '20px',
  },
  dialogActions: {
    paddingX: '32px !important',
    marginBottom: '1rem',
  },
  buttonStack: {
    flexGrow: 1,
    justifyContent: 'center',
    gap: 2,
  },
  button: {
    width: '30%',
  },
  confirmButton: {
    color: (theme) => theme.palette.text.primary,
    width: '30%',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: (theme) => theme.shadows[1],
    },
  },
};
